import React, { useState, useEffect } from "react";
import "../styles/Expense.css";
import API_URL from "../apiConfig";

const DEFAULT_ACCOUNT_HEADS = [
  "SALARY - Teaching Staff", "SALARY - Non Teaching Staff", "GUEST LECTURER",
  "Printing & Stationary", "Office Rent", "Bus Rent", "College Uniform",
  "Maintenance & Repair", "Electricity Bill", "Telephone & Postage", "Food & Nutrition",
  "Travelling Expense", "Tea & Beverage", "Medicine & Drugs", "Advertisement",
  "Annual Social & Cultural Programme", "Water", "Furniture", "College Affiliation Fee",
  "Fee Regulating Authority", "Tution Fee Return", "Audit Fee", "Bank Charge"
];

const MODE_OPTIONS = ["cash", "bank", "online", "UPI", "scholarship", "Carryforward"];

const initialState = {
  voucher_no: "",
  date: "",
  account_head: "",
  particulars: "",
  reference: "",
  amount: "",
  mode: "",
};

function parseAmountInput(value) {
  if (value === null || value === undefined) return NaN;
  const s = String(value).trim().replace(/,/g, "");
  if (s === "") return NaN;
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
}

export default function ExpenseEntryModal({
  token,
  expense,
  editMode,
  onClose,
  onSave,
  heads: headsFromProps,
  onAddHead: onAddHeadProp
}) {
  const [form, setForm] = useState(initialState);
  const [heads, setHeads] = useState(DEFAULT_ACCOUNT_HEADS.slice());
  const [addingHead, setAddingHead] = useState(false);
  const [newHead, setNewHead] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [apiDetails, setApiDetails] = useState(null);

  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("expense_heads") || "[]");
      const merged = Array.from(new Set([...(headsFromProps || []), ...stored, ...DEFAULT_ACCOUNT_HEADS]));
      setHeads(merged);
    } catch {
      setHeads(Array.from(new Set([...(headsFromProps || []), ...DEFAULT_ACCOUNT_HEADS])));
    }
  // eslint-disable-next-line
  }, [headsFromProps]);

  useEffect(() => {
    if (editMode && expense) {
      setForm({
        voucher_no: expense.voucher_no || "",
        date: expense.date ? (expense.date.slice(0,10)) : "",
        account_head: expense.account_head || "",
        particulars: expense.particulars || "",
        reference: expense.reference || "",
        amount: expense.amount !== undefined && expense.amount !== null ? String(expense.amount) : "",
        mode: expense.mode || "",
      });
    } else {
      setForm(initialState);
    }
    setMessage("");
    setApiDetails(null);
  }, [editMode, expense]);

  const setField = (name, value) => {
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const saveNewHead = () => {
    const trimmed = String(newHead || "").trim();
    if (!trimmed) return;
    if (typeof onAddHeadProp === "function") {
      try { onAddHeadProp(trimmed); } catch (e) { /* ignore */ }
    } else {
      try {
        const existing = JSON.parse(localStorage.getItem("expense_heads") || "[]");
        if (!existing.includes(trimmed)) {
          existing.push(trimmed);
          localStorage.setItem("expense_heads", JSON.stringify(existing));
        }
      } catch { /* ignore */ }
    }
    setHeads(prev => Array.from(new Set([trimmed, ...prev])));
    setForm(prev => ({ ...prev, account_head: trimmed }));
    setNewHead("");
    setAddingHead(false);
  };

  const handleSubmit = async (e) => {
    e && e.preventDefault();
    setMessage("");
    setApiDetails(null);

    if (!form.date) { setMessage("Date is required."); return; }
    if (!form.account_head) { setMessage("Account Head is required."); return; }
    if (!form.mode) { setMessage("Mode is required."); return; }
    const amt = parseAmountInput(form.amount);
    if (!Number.isFinite(amt)) { setMessage("Enter a valid numeric Amount."); return; }

    setLoading(true);
    try {
      const payload = {
        voucher_no: editMode ? form.voucher_no : undefined,
        date: form.date,
        account_head: form.account_head,
        particulars: form.particulars || "",
        reference: form.reference || "",
        amount: amt,
        mode: form.mode
      };

      const url = editMode && expense
        ? `${API_URL}/api/income_expense/expense/${expense.id}`
        : `${API_URL}/api/income_expense/expense`;
      const method = editMode ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });

      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (body && (body.details || body.error)) {
          setMessage(body.error || "Validation failed");
          setApiDetails(body.details || null);
        } else {
          setMessage(body.error || body.message || `Server returned ${res.status}`);
        }
        setLoading(false);
        return;
      }

      if (!editMode && body && body.voucher_no) {
        setMessage(`Expense added (Voucher: ${body.voucher_no})`);
      } else {
        setMessage(editMode ? "Expense updated" : "Expense added");
      }

      if (typeof onSave === "function") {
        try {
          const maybe = onSave();
          if (maybe && typeof maybe.then === "function") await maybe;
        } catch (err) {
          console.error("onSave error", err);
        }
      }
      if (typeof onClose === "function") onClose();
    } catch (err) {
      console.error("Expense save error:", err);
      setMessage("Failed to save expense due to network/server error.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="expense-modal-bg" role="dialog" aria-modal="true">
      <div className="expense-modal">
        <form className="expense-form" onSubmit={handleSubmit}>
          <h3>{editMode ? "Edit Expense" : "Add Expense"}</h3>

          <div className="row">
            <label>Voucher No *</label>
            <input
              type="text"
              name="voucher_no"
              value={editMode ? form.voucher_no : (form.voucher_no || "Auto-generated")}
              readOnly
              style={{ background: "#f5f5f5", color: "#333" }}
            />
          </div>

          <div className="row">
            <label>Date *</label>
            <input type="date" name="date" value={form.date} onChange={e => setField("date", e.target.value)} required />
          </div>

          <div className="row">
            <label>Account Head *</label>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <select
                name="account_head"
                value={form.account_head}
                onChange={e => {
                  const v = e.target.value;
                  if (v === "__add_new__") { setAddingHead(true); setNewHead(""); }
                  else setField("account_head", v);
                }}
                required
              >
                <option value="">-- Select Head --</option>
                {heads.map(h => <option key={h} value={h}>{h}</option>)}
                <option value="__add_new__">+ Add new head...</option>
              </select>

              {addingHead && (
                <>
                  <input
                    type="text"
                    placeholder="New head name"
                    value={newHead}
                    onChange={e => setNewHead(e.target.value)}
                  />
                  <button type="button" onClick={saveNewHead}>Save Head</button>
                  <button type="button" onClick={() => { setAddingHead(false); setNewHead(""); }}>Cancel</button>
                </>
              )}
            </div>
          </div>

          <div className="row">
            <label>Particulars</label>
            <input type="text" name="particulars" value={form.particulars} onChange={e => setField("particulars", e.target.value)} />
          </div>

          <div className="row">
            <label>Reference</label>
            <input type="text" name="reference" value={form.reference} onChange={e => setField("reference", e.target.value)} />
          </div>

          <div className="row">
            <label>Amount (₹) *</label>
            <input
              type="text"
              name="amount"
              value={form.amount}
              onChange={e => setField("amount", e.target.value)}
              placeholder="e.g. 1,000.00"
              required
            />
          </div>

          <div className="row">
            <label>Mode *</label>
            <select name="mode" value={form.mode} onChange={e => setField("mode", e.target.value)} required>
              <option value="">-- Select Mode --</option>
              {MODE_OPTIONS.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>

          {apiDetails && (
            <div className="error-box" style={{ color: "crimson", marginTop: 8 }}>
              <strong>Validation details:</strong>
              <pre style={{ whiteSpace: "pre-wrap", margin: 4 }}>{JSON.stringify(apiDetails, null, 2)}</pre>
            </div>
          )}

          {message && (
            <div className={`expense-message ${message.toLowerCase().includes("failed") ? "error" : "success"}`} style={{ marginTop: 8 }}>
              {message}
            </div>
          )}

          <div className="expense-actions" style={{ marginTop: 12, display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button type="button" onClick={() => { if (!loading) onClose?.(); }} disabled={loading}>Cancel</button>
            <button type="submit" disabled={loading}>{loading ? "Saving..." : (editMode ? "Update" : "Add")}</button>
          </div>
        </form>
      </div>
    </div>
  );
}